<template>
  <div v-if="list.length > 0"
       class="product-grid-list-component"
       :class="[
         `size-${size}`,
         { 'is-no-swiper': !showSwiper },
       ]"
  >
    <swiper v-if="showSwiper && !showCardGrid"
            class="product-grid-list"
            :options="swiperOption"
            ref="swiper"
            @click.native="handleClickSlider"
    >
      <swiper-slide class="grid-swiper-slide"
                    v-for="(item, index) in list"
                    :key="index"
      >
        <item class="product-grid-list-item"
              :item="item"
              :size="itemSize"
              :show-title="showTitle"
              :show-summary="showSummary"
        />
      </swiper-slide>
    </swiper>
    <div v-else
         class="grid-list"
         :class="{ 'card-grid': showCardGrid }"
    >
      <item class="product-grid-list-item"
            v-for="(item, index) in list"
            :key="index"
            :item="item"
            :size="itemSize"
            v-ym-report:[recordName]="fetchRecordParams(item)"
            @click.native="handleClick(item)"
      />
    </div>
  </div>
  <div v-else
       class="empty"
  >
    {{ emptyString }}
  </div>
</template>

<script>
  import { swiper, swiperSlide } from 'vue-awesome-swiper';
  import { decorator, before } from 'ym-reporter-vue';
  import Item from '@/components/ProductGrid/Item';
  import { getProductPath, locationHref, projectJump } from '@/utils';
  import { PRODUCT_CLICK_MAP, PRODUCT_EXPOSURE_MAP } from '@/utils/constant';
  import SwiperLoopMixin from '@/mixins/swiper/loop';

  export default {
    name: 'ProductGridList',
    mixins: [SwiperLoopMixin],
    components: {
      Item,
      swiper,
      swiperSlide,
    },
    data() {
      return {
        swiperOption: {
          slidesPerView: 'auto',
          spaceBetween: 15,
        },
      };
    },
    props: {
      list: {
        type: Array,
        required: true,
      },
      showSwiper: {
        type: Boolean,
        default: true,
      },
      showCardGrid: {
        type: Boolean,
        required: true,
      },
      options: {
        type: Object,
      },
      size: {
        type: String,
        validator(val) {
          return [
            'small',
            'large',
            'fullScreen',
            'card',
          ].includes(val);
        },
        default: 'small',
      },
      emptyString: {
        type: String,
        default: '暂无数据',
      },
      showTitle: {
        type: Boolean,
        default: true,
      },
      showSummary: {
        type: Boolean,
        default: true,
      },
      recordType: {
        type: String,
        validator(val) {
          return [
            'main',
            'audio',
            'course',
            'parent',
            'learning',
            'studentGains',
            'customCollection',
          ].includes(val);
        },
      },
      blockTitle: {
        type: String,
        default: '',
      },
      customCollectionType: {
        type: String,
        default: '',
      },
    },
    computed: {
      itemSize() {
        const { size } = this;
        if (size === 'fullScreen') return 'full';
        return size;
      },
      swiperRefs() {
        return this.$refs.swiper.swiper;
      },
      recordName() {
        const { recordType } = this;
        if (recordType === 'learning') return '';
        if (recordType == null) return recordType;
        return `${this.recordType}_${PRODUCT_EXPOSURE_MAP[this.size]}`.toUpperCase();
      },
      clickRecordName() {
        const { recordType } = this;
        if (recordType === 'learning') return '';
        if (recordType == null) return recordType;
        const clickType = `${this.recordType}_${PRODUCT_CLICK_MAP[this.size]}`;
        return `${clickType}`.toUpperCase();
      },
    },
    watch: {
      options(val) {
        this.setOptions(val);
      },
    },
    methods: {
      setOptions(val) {
        this.swiperOption = Object.assign({}, this.swiperOption, val);
      },
      fetchRecordParams(item) {
        const { productType, id } = item;
        const { customCollectionType } = this;
        const logParams = {
          name: this.blockTitle,
          targetType: productType,
          targetId: id,
        };
        const customCollectionParams = customCollectionType === '' ? {} : { customCollectionType };
        return Object.assign(item, logParams, customCollectionParams);
      },

      @decorator(before(function (item) {
        if (this.clickRecordName != null) {
          const adjustItem = this.fetchRecordParams(item);
          const params = Object.assign(adjustItem, {
            ymReporterRecordEntrySource: true,
          });
          this.$reporter.report(this.clickRecordName, params);
        }
      }))
      handleClick(item) {
        const { productType, id, productUrl } = item;
        if (productUrl && productUrl !== '') {
          locationHref(productUrl);
          return;
        }
        const route = getProductPath(productType, id);
        projectJump(route);
      },
      handleClickSlider() {
        const { swiperRefs, swiperOption } = this;
        const { loop } = swiperOption;
        const { realIndex, clickedIndex } = swiperRefs;
        const currentIndex = loop ? realIndex : clickedIndex;
        const item = this.list.find((i, index) => index === currentIndex);
        this.handleClick(item);
      },
    },
    created() {
      this.setOptions(this.options);
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .product-grid-list-component {
    padding-top: @size-base;
    padding-bottom: @size-large;
    &.size-small {
      padding-left: @size-base;
    }
    &.size-fullScreen {
      padding-left: @size-base;
      padding-right: @size-base;
    }
  }

  .product-grid-list {
    z-index: @z-index-base;
  }

  .is-no-swiper {
    .product-grid-list-item {
      &:not(:first-child) {
        margin-top: @size-large;
      }
    }
  }

  .grid-swiper-slide {
    width: auto;
  }

  .swiper-pagination {
    position: static;
    font-size: 0;
    line-height: 1;
    margin-top: @size-base;
  }

  .grid-list {
    &.card-grid {
      box-sizing: border-box;
      padding: 0 @size-base;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;

      .product-grid-list-item {
        margin-top: @size-large;
        &:first-child {
          margin-top: 0;
        }
        &:nth-child(2) {
          margin-top: 0;
        }
      }
    }
  }
</style>
